<script setup>
import * as d3 from 'd3';
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import ScreenshotDialog from '@/src/components/ScreenshotDialog.vue';
import { useImageClipboardStore } from '@/src/store/image-clipboard-store';

const props = defineProps({
  title: {
    type: String,
    default: 'Flow Rate Over Time',
  },
  /**
   * Can be either:
   *  - Single series (backwards compatible):
   *      [ { time, flow }, ... ]
   *  - Up to 5 series:
   *      [
   *        { id?: string, color: string, data: [ { time, flow }, ... ] },
   *        ...
   *      ]
   */
  data: {
    type: Array,
    default: () => [],
  },
  xLabel: {
    type: String,
    default: 'Time (ms)',
  },
  yLabel: {
    type: String,
    default: 'Flow Rate (ml/s)',
  },
  // Fallback color for single-series or series without color
  color: {
    type: String,
    default: '#ffffff',
  },
  showPoints: {
    type: Boolean,
    default: true,
  },
  showGrid: {
    type: Boolean,
    default: true,
  },
  /**
   * Optional vertical marker position along the x-axis (same unit as time).
   * If null/undefined, no marker is drawn.
   */
  markerX: {
    type: Number,
    default: 0,
  },
  /**
   * Optional draggable vertical rulers (same unit as time).
   * When dragged, emits `update:leftRulerX` / `update:rightRulerX`.
   * Left must not be to the right of right.
   */
  leftRulerX: {
    type: Number,
    default: 0,
  },
  rightRulerX: {
    type: Number,
    default: 0,
  },
  /**
   * Optional net-volume summary for the ruler window, shown in the upper-right
   * quadrant. Each entry: { id, color, inside, outside } (volumes in ml).
   */
  rulerVolumeSummary: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'update:leftRulerX',
  'update:rightRulerX',
  'update:markerX',
]);

const chartContainer = ref(null);
const screenshotDialog = ref(false);
const screenshotDataUrl = ref('');
const screenshotDimensions = ref({ width: 0, height: 0 });
const imageClipboardStore = useImageClipboardStore();

let resizeObserver = null;
let xScale = null;
let yScale = null;

// store chart dimensions for fast marker/ruler updates
let chartWidth = 0;
let chartHeight = 0;

// x-domain for clamping rulers/band
let chartMinX = 0;
let chartMaxX = 1;

// gradient suffix to keep IDs unique per component
const gradientSuffix = Math.random().toString(36).slice(2, 9);

const margin = { top: 20, right: 40, bottom: 40, left: 60 };

// bisector for nearest x (time)
const bisectTime = d3.bisector(d => d.time).left;

// dragging state for rulers
let draggingRuler = null; // 'left' | 'right' | null

// dragging state for horizontal band
let draggingBand = false;
let bandDragStartMouseX = 0;
let bandInitialLeft = 0;
let bandInitialRight = 0;

// internal ruler positions used for drawing
const internalLeftRulerX = ref(props.leftRulerX);
const internalRightRulerX = ref(props.rightRulerX);

function formatValue(value, decimals = 2) {
  if (value === null || value === undefined || Number.isNaN(value))
    return 'N/A';
  return Number(value).toFixed(decimals);
}

// Fixed-width volume so the upper-right summary doesn't reflow/flicker as
// digits are gained or lost while dragging the rulers (fits "-99.9").
function formatVolume(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '  N/A';
  }
  return Number(value).toFixed(1).padStart(5, ' ');
}

function handleScreenshotSave(screenshotData) {
  imageClipboardStore.addImage({
    imageDataURL: screenshotData.imageDataURL,
    title: screenshotData.title,
    description: screenshotData.description,
    tags: screenshotData.tags,
    viewportId: 'Flow-Diagram',
    width: screenshotDimensions.value.width,
    height: screenshotDimensions.value.height,
  });
}

function handleScreenshotDiscard() {
  screenshotDataUrl.value = '';
}

/**
 * Rebuild the chart onto a fixed-size offscreen canvas (same approach as
 * AnalysisGraph) and open the screenshot dialog.
 */
async function takeScreenshot() {
  const seriesArr = getSeriesArray();
  if (seriesArr.length === 0) return;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 700;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const m = { top: 60, right: 60, bottom: 80, left: 100 };
    const width = canvas.width - m.left - m.right;
    const height = canvas.height - m.top - m.bottom;

    const processedSeries = seriesArr.map(series => ({
      ...series,
      processedData: withZeroCrossings(series.data),
    }));
    const allPoints = seriesArr.flatMap(s => s.data);
    if (allPoints.length === 0) return;

    const minTime = d3.min(allPoints, d => d.time) ?? 0;
    const maxTime = d3.max(allPoints, d => d.time) ?? 1;
    const sx = d3.scaleLinear().domain([minTime, maxTime]).range([0, width]);

    let yMin = d3.min(allPoints, d => d.flow) ?? 0;
    let yMax = d3.max(allPoints, d => d.flow) ?? 0;
    if (yMin === yMax) {
      yMin -= 1;
      yMax += 1;
    }
    const span = yMax - yMin;
    yMin -= span * 0.1;
    yMax += span * 0.1;
    const sy = d3.scaleLinear().domain([yMin, yMax]).range([height, 0]);

    const toX = t => m.left + sx(t);
    const toY = f => m.top + sy(f);

    // Grid
    if (props.showGrid) {
      ctx.save();
      ctx.strokeStyle = '#d0cfe5';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.globalAlpha = 0.7;
      sx.ticks(8).forEach((tick) => {
        const x = toX(tick);
        ctx.beginPath();
        ctx.moveTo(x, m.top);
        ctx.lineTo(x, m.top + height);
        ctx.stroke();
      });
      sy.ticks(8).forEach((tick) => {
        const y = toY(tick);
        ctx.beginPath();
        ctx.moveTo(m.left, y);
        ctx.lineTo(m.left + width, y);
        ctx.stroke();
      });
      ctx.restore();
    }

    // Zero baseline
    if (yMin < 0 && yMax > 0) {
      ctx.save();
      ctx.strokeStyle = '#B0B0C0';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.globalAlpha = 0.7;
      const zeroY = toY(0);
      ctx.beginPath();
      ctx.moveTo(m.left, zeroY);
      ctx.lineTo(m.left + width, zeroY);
      ctx.stroke();
      ctx.restore();
    }

    let baselineValue;
    if (yMin < 0 && yMax > 0) baselineValue = 0;
    else if (yMax <= 0) baselineValue = yMax;
    else baselineValue = yMin;
    const baselineY = toY(baselineValue);

    // Series: area fills + lines + points
    processedSeries.forEach((series) => {
      const baseColor = series.color || props.color;
      const data = series.processedData;

      const areaPositive = d3
        .area()
        .x(d => toX(d.time))
        .y0(baselineY)
        .y1(d => toY(d.flow))
        .defined(d => d.flow >= 0)
        .curve(d3.curveMonotoneX);
      const areaNegative = d3
        .area()
        .x(d => toX(d.time))
        .y0(baselineY)
        .y1(d => toY(d.flow))
        .defined(d => d.flow <= 0)
        .curve(d3.curveMonotoneX);
      const line = d3
        .line()
        .x(d => toX(d.time))
        .y(d => toY(d.flow))
        .curve(d3.curveMonotoneX);

      const posPath = areaPositive(data);
      const negPath = areaNegative(data);
      const linePath = line(data);

      ctx.save();
      if (posPath) {
        ctx.fillStyle = baseColor;
        ctx.globalAlpha = 0.25;
        ctx.fill(new Path2D(posPath));
      }
      if (negPath) {
        ctx.fillStyle = baseColor;
        ctx.globalAlpha = 0.2;
        ctx.fill(new Path2D(negPath));
      }
      ctx.globalAlpha = 1;
      if (linePath) {
        ctx.strokeStyle = baseColor;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke(new Path2D(linePath));
      }
      ctx.restore();

      if (props.showPoints && series.data?.length) {
        series.data.forEach((d) => {
          ctx.beginPath();
          ctx.arc(toX(d.time), toY(d.flow), 4, 0, Math.PI * 2);
          ctx.fillStyle = baseColor;
          ctx.fill();
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 1;
          ctx.stroke();
        });
      }
    });

    // Axes
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top + height);
    ctx.lineTo(m.left + width, m.top + height);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(m.left, m.top);
    ctx.lineTo(m.left, m.top + height);
    ctx.stroke();

    ctx.fillStyle = 'black';
    ctx.font = '18px Arial';
    ctx.textAlign = 'center';
    sx.ticks(8).forEach((tick) => {
      const x = toX(tick);
      ctx.beginPath();
      ctx.moveTo(x, m.top + height);
      ctx.lineTo(x, m.top + height + 6);
      ctx.stroke();
      ctx.fillText(String(Math.round(tick)), x, m.top + height + 24);
    });
    ctx.textAlign = 'right';
    sy.ticks(6).forEach((tick) => {
      const y = toY(tick);
      ctx.beginPath();
      ctx.moveTo(m.left, y);
      ctx.lineTo(m.left - 6, y);
      ctx.stroke();
      ctx.fillText(tick.toFixed(1), m.left - 10, y + 6);
    });

    // Axis labels
    ctx.fillStyle = '#5F5F6B';
    ctx.font = '500 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(props.xLabel, m.left + width / 2, canvas.height - 24);
    ctx.save();
    ctx.translate(28, m.top + height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(props.yLabel, 0, 0);
    ctx.restore();

    // Marker
    if (
      props.markerX !== null
      && props.markerX !== undefined
      && !Number.isNaN(props.markerX)
    ) {
      const mx = toX(props.markerX);
      ctx.save();
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(mx, m.top);
      ctx.lineTo(mx, m.top + height);
      ctx.stroke();
      ctx.restore();

      seriesArr.forEach((series) => {
        const yVal = getYAtTime(series.data, props.markerX);
        if (yVal === null) return;
        ctx.beginPath();
        ctx.arc(mx, toY(yVal), 5, 0, Math.PI * 2);
        ctx.fillStyle = series.color || props.color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });
    }

    // Rulers + band
    const { left, right } = getEffectiveRulerPositions();
    const drawRulerLine = (xValue) => {
      const rx = toX(xValue);
      ctx.save();
      ctx.strokeStyle = '#444';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(rx, m.top);
      ctx.lineTo(rx, m.top + height);
      ctx.stroke();
      ctx.restore();

      seriesArr.forEach((series) => {
        const yVal = getYAtTime(series.data, xValue);
        if (yVal === null) return;
        ctx.beginPath();
        ctx.arc(rx, toY(yVal), 4, 0, Math.PI * 2);
        ctx.fillStyle = series.color || props.color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    };
    if (left !== null && right !== null) {
      ctx.save();
      ctx.fillStyle = 'rgba(100, 100, 140, 0.08)';
      ctx.fillRect(toX(left), m.top, toX(right) - toX(left), height);
      ctx.restore();
    }
    if (left !== null) drawRulerLine(left);
    if (right !== null) drawRulerLine(right);

    // Ruler volume summary (upper right)
    if (props.rulerVolumeSummary?.length) {
      let textY = m.top + 6;
      const textX = canvas.width - m.right - 8;
      ctx.font = '14px Arial';
      props.rulerVolumeSummary.forEach((entry) => {
        const label = `${entry.id}  in${formatVolume(entry.inside)}ml  out${formatVolume(entry.outside)}ml`;
        const swatchX = textX - ctx.measureText(label).width - 14;
        ctx.fillStyle = entry.color || '#888';
        ctx.fillRect(swatchX, textY - 8, 8, 8);
        ctx.fillStyle = '#5F5F6B';
        ctx.textAlign = 'right';
        ctx.fillText(label, textX, textY);
        textY += 20;
      });
    }

    // Legend (top left)
    if (seriesArr.length > 1) {
      let legendY = m.top + 6;
      const legendX = m.left + 12;
      seriesArr.forEach((series) => {
        ctx.fillStyle = series.color || props.color;
        ctx.fillRect(legendX, legendY + 2, 28, 4);
        ctx.fillStyle = 'black';
        ctx.font = '14px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(series.id, legendX + 36, legendY + 8);
        legendY += 22;
      });
    }

    screenshotDataUrl.value = canvas.toDataURL('image/png');
    screenshotDimensions.value = {
      width: canvas.width,
      height: canvas.height,
    };
    screenshotDialog.value = true;
  }
  catch (error) {
    console.error('Error taking flow diagram screenshot:', error);
  }
}

/**
 * Normalize props.data to an array of series:
 * [
 *   { id: string, color: string, data: [{ time, flow }] },
 *   ...
 * ]
 */
function getSeriesArray() {
  if (!Array.isArray(props.data) || props.data.length === 0) return [];

  const first = props.data[0];

  // Single-series mode: [{ time, flow }, ...]
  if (
    first
    && typeof first === 'object'
    && 'time' in first
    && 'flow' in first
  ) {
    return [
      {
        id: 'Series 1',
        color: props.color,
        data: props.data,
      },
    ];
  }

  // Multi-series mode: [{ id?, color, data: [...] }, ...] (max 5)
  return props.data.slice(0, 5).map((s, idx) => ({
    id: s.id || `Series ${idx + 1}`,
    color: s.color || props.color,
    data: Array.isArray(s.data) ? s.data : [],
  }));
}

/**
 * Insert interpolated zero-crossing points where sign changes between samples.
 * Returns a *new* array and does not mutate the original data.
 */
function withZeroCrossings(data) {
  if (!data || data.length === 0) return [];

  const result = [];

  for (let i = 0; i < data.length - 1; i++) {
    const p0 = data[i];
    const p1 = data[i + 1];

    result.push(p0);

    // If either is exactly zero, no need to interpolate for that segment
    if (p0.flow === 0 || p1.flow === 0) {
      continue;
    }

    // Check if there's a sign change between p0 and p1
    if ((p0.flow > 0 && p1.flow < 0) || (p0.flow < 0 && p1.flow > 0)) {
      const t0 = p0.time;
      const t1 = p1.time;
      const f0 = p0.flow;
      const f1 = p1.flow;

      // linear interpolation for flow=0:
      // f(t) = f0 + (f1 - f0)*r => 0 = f0 + (f1 - f0)*r => r = f0 / (f0 - f1)
      const ratio = f0 / (f0 - f1);
      const tZero = t0 + (t1 - t0) * ratio;

      result.push({ time: tZero, flow: 0 });
    }
  }

  // push last original point
  result.push(data[data.length - 1]);

  return result;
}

/**
 * Get interpolated y at given time for a series' data (linear interpolation).
 */
function getYAtTime(data, time) {
  if (!data || data.length === 0) return null;

  const i = bisectTime(data, time);
  const p0 = data[i - 1];
  const p1 = data[i];

  if (!p0 && !p1) return null;
  if (!p0) return p1.flow;
  if (!p1) return p0.flow;

  const t0 = p0.time;
  const t1 = p1.time;
  if (t1 === t0) return p0.flow;

  const ratio = (time - t0) / (t1 - t0);
  return p0.flow + (p1.flow - p0.flow) * ratio;
}

/**
 * Draw or update the vertical marker line inside the given svg group.
 */
function drawMarkerLine(svg) {
  if (!xScale || chartWidth <= 0 || chartHeight <= 0) return;

  // remove old marker line if any
  svg.select('#marker-line').remove();

  if (
    props.markerX === null
    || props.markerX === undefined
    || Number.isNaN(props.markerX)
  ) {
    return; // nothing to draw
  }

  const xPos = xScale(props.markerX);
  if (xPos < 0 || xPos > chartWidth) return;

  svg
    .append('line')
    .attr('id', 'marker-line')
    .attr('x1', xPos)
    .attr('x2', xPos)
    .attr('y1', 0)
    .attr('y2', chartHeight)
    .attr('stroke', 'var(--md-primary, #6200EE)')
    .attr('stroke-width', 1)
    .attr('opacity', 0.9)
    .attr('pointer-events', 'none');
}

/**
 * Small white indicators where marker crosses the curves.
 */
function drawMarkerIndicators(svg, seriesArr) {
  if (!xScale || !yScale || chartWidth <= 0 || chartHeight <= 0) return;

  svg.select('#marker-indicators').remove();

  if (
    props.markerX === null
    || props.markerX === undefined
    || Number.isNaN(props.markerX)
  ) {
    return;
  }

  const xPos = xScale(props.markerX);
  if (xPos < 0 || xPos > chartWidth) return;

  const group = svg.append('g').attr('id', 'marker-indicators');

  seriesArr.forEach((series) => {
    if (!series.data || !series.data.length) return;

    const yVal = getYAtTime(series.data, props.markerX);
    if (yVal === null || Number.isNaN(yVal)) return;

    const baseColor = series.color || props.color;
    const py = yScale(yVal);

    group
      .append('circle')
      .attr('cx', xPos)
      .attr('cy', py)
      .attr('r', 4)
      .attr('fill', '#FFFFFF') // marker white (fill)
      .attr('stroke', baseColor)
      .attr('stroke-width', 1.2)
      .attr('pointer-events', 'none');
  });
}

/**
 * Fast marker-only update, used when markerX changes.
 */
function updateMarkerLineAndIndicators() {
  if (!chartContainer.value || !xScale || chartWidth <= 0 || chartHeight <= 0)
    return;

  const svg = d3.select(chartContainer.value).select('svg').select('g'); // main chart group

  if (svg.empty()) return;

  const seriesArr = getSeriesArray();
  drawMarkerLine(svg);
  drawMarkerIndicators(svg, seriesArr);
}

/**
 * Compute effective left/right ruler positions with constraint:
 * left <= right (when both defined).
 * Uses internal refs so dragging is visible immediately.
 */
function getEffectiveRulerPositions() {
  const leftVal = internalLeftRulerX.value;
  const rightVal = internalRightRulerX.value;

  const hasLeft
    = leftVal !== null && leftVal !== undefined && !Number.isNaN(leftVal);
  const hasRight
    = rightVal !== null && rightVal !== undefined && !Number.isNaN(rightVal);

  if (!hasLeft && !hasRight) {
    return { left: null, right: null };
  }

  if (hasLeft && !hasRight) {
    return { left: leftVal, right: null };
  }

  if (!hasLeft && hasRight) {
    return { left: null, right: rightVal };
  }

  // both defined -> enforce left <= right
  if (leftVal > rightVal) {
    return { left: rightVal, right: rightVal };
  }

  return { left: leftVal, right: rightVal };
}

/**
 * Draw or update the vertical rulers (left & right) inside the given svg group.
 * Also attaches drag handles and draws small indicators + labels where they cross curves,
 * and a horizontal band between them.
 */
function drawRulers(svg, seriesArr) {
  if (!xScale || !yScale || chartWidth <= 0 || chartHeight <= 0) return;

  // remove old ruler lines/handles if any
  svg.select('#rulers-group').remove();

  const { left, right } = getEffectiveRulerPositions();

  if (left === null && right === null) return;

  const group = svg.append('g').attr('id', 'rulers-group');

  // Helper to add a visible line + invisible wide hit area with drag handler
  const addRuler = (idBase, xValue, isLeft) => {
    const xPos = xScale(xValue);
    if (xPos < 0 || xPos > chartWidth) return;

    const lineId = `${idBase}-line`;
    const hitId = `${idBase}-hit`;

    // visible line (both rulers solid)
    group
      .append('line')
      .attr('id', lineId)
      .attr('x1', xPos)
      .attr('x2', xPos)
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', 'var(--md-primary, #6200EE)')
      .attr('stroke-width', 1.4)
      .attr('stroke-dasharray', null)
      .attr('opacity', 0.9)
      .attr('pointer-events', 'none');

    // per-series indicators + labels where ruler crosses curves
    const indicators = group.append('g').attr('class', `${idBase}-indicators`);

    seriesArr.forEach((series) => {
      if (!series.data || !series.data.length) return;

      const yVal = getYAtTime(series.data, xValue);
      if (yVal === null || Number.isNaN(yVal)) return;

      const baseColor = series.color || props.color;
      const py = yScale(yVal);

      // small circle at crossing
      indicators
        .append('circle')
        .attr('cx', xPos)
        .attr('cy', py)
        .attr('r', 3)
        .attr('fill', baseColor)
        .attr('stroke', '#FFFFFF')
        .attr('stroke-width', 0.8)
        .attr('pointer-events', 'none');

      // label text: "123ms, 45.6 ml/s"
      const labelGroup = indicators.append('g');

      // side: if on left half -> text to the right; otherwise to the left
      const anchorRightSide = xPos < chartWidth / 2;
      const dx = anchorRightSide ? 6 : -6;
      const textAnchor = anchorRightSide ? 'start' : 'end';

      let ty = py - 6;
      // if too high at top, push down slightly
      if (ty < 10) ty = py + 12;

      const label = labelGroup
        .append('text')
        .attr('x', xPos + dx)
        .attr('y', ty)
        .attr('text-anchor', textAnchor)
        .attr('dominant-baseline', 'middle')
        .style('font-size', '10px')
        .style('fill', 'var(--md-on-surface-variant, #5F5F6B)')
        .text(`${formatValue(xValue, 0)}ms, ${formatValue(yVal, 1)} ml/s`)
        .attr('pointer-events', 'none');

      // small translucent background behind the label
      const bbox = label.node()?.getBBox();
      if (bbox) {
        labelGroup
          .insert('rect', 'text')
          .attr('x', bbox.x - 3)
          .attr('y', bbox.y - 2)
          .attr('width', bbox.width + 6)
          .attr('height', bbox.height + 4)
          .attr('rx', 3)
          .attr('ry', 3)
          .attr('fill', 'rgba(0, 0, 0, 0.2)')
          .attr('stroke', 'none')
          .attr('pointer-events', 'none');
      }
    });

    // invisible wide hit area for easier dragging
    group
      .append('line')
      .attr('id', hitId)
      .attr('x1', xPos)
      .attr('x2', xPos)
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke-width', 12)
      .attr('stroke', 'transparent')
      .style('cursor', 'ew-resize')
      .on('mousedown', event =>
        startRulerDrag(isLeft ? 'left' : 'right', event));
  };

  if (left !== null) {
    addRuler('ruler-left', left, true);
  }

  if (right !== null) {
    addRuler('ruler-right', right, false);
  }

  // Horizontal band connecting left & right, slightly above x-axis
  if (
    left !== null
    && right !== null
    && right > left
    && chartWidth > 0
    && chartHeight > 0
  ) {
    const xLeft = xScale(left);
    const xRight = xScale(right);
    const bandY = chartHeight - 12; // a bit above the x-axis

    const bandId = 'rulers-band';
    const bandHitId = 'rulers-band-hit';

    // visible band line
    group
      .append('line')
      .attr('id', bandId)
      .attr('x1', xLeft)
      .attr('x2', xRight)
      .attr('y1', bandY)
      .attr('y2', bandY)
      .attr('stroke', 'var(--md-primary, #6200EE)')
      .attr('stroke-width', 1)
      .attr('opacity', 0.3)
      .attr('pointer-events', 'none');

    // invisible wide hit area for dragging both rulers at once
    group
      .append('line')
      .attr('id', bandHitId)
      .attr('x1', xLeft)
      .attr('x2', xRight)
      .attr('y1', bandY)
      .attr('y2', bandY)
      .attr('stroke-width', 12)
      .attr('stroke', 'transparent')
      .style('cursor', 'grab')

      .on('mousedown', event => startBandDrag(event));
  }
}

/**
 * Fast rulers-only update, used when internal rulers or props change.
 */
function updateRulers() {
  if (!chartContainer.value || !xScale || chartWidth <= 0 || chartHeight <= 0)
    return;

  const svg = d3.select(chartContainer.value).select('svg').select('g'); // main chart group

  if (svg.empty()) return;

  const seriesArr = getSeriesArray();
  drawRulers(svg, seriesArr);
}

/**
 * Drag handlers for individual rulers
 */
function onRulerDrag(event) {
  if (!draggingRuler || !chartContainer.value || !xScale) return;

  const rect = chartContainer.value.getBoundingClientRect();
  let mouseX = event.clientX - rect.left - margin.left;

  if (mouseX < 0) mouseX = 0;
  if (mouseX > chartWidth) mouseX = chartWidth;

  const time = xScale.invert(mouseX);

  if (draggingRuler === 'left') {
    let newLeft = time;
    const rightVal = internalRightRulerX.value;
    if (
      rightVal !== null
      && rightVal !== undefined
      && !Number.isNaN(rightVal)
      && newLeft > rightVal
    ) {
      newLeft = rightVal;
    }
    if (newLeft < chartMinX) newLeft = chartMinX;

    internalLeftRulerX.value = newLeft; // move visually
    emit('update:leftRulerX', newLeft); // notify parent
  }
  else if (draggingRuler === 'right') {
    let newRight = time;
    const leftVal = internalLeftRulerX.value;
    if (
      leftVal !== null
      && leftVal !== undefined
      && !Number.isNaN(leftVal)
      && newRight < leftVal
    ) {
      newRight = leftVal;
    }
    if (newRight > chartMaxX) newRight = chartMaxX;

    internalRightRulerX.value = newRight; // move visually
    emit('update:rightRulerX', newRight); // notify parent
  }

  // redraw rulers at new internal positions (including indicators + labels + band)
  updateRulers();
}

function stopRulerDrag() {
  if (!draggingRuler) return;
  draggingRuler = null;
  window.removeEventListener('mousemove', onRulerDrag);
  window.removeEventListener('mouseup', stopRulerDrag);
}

function startRulerDrag(type, event) {
  if (!chartContainer.value || !xScale) return;
  draggingRuler = type; // 'left' or 'right'
  window.addEventListener('mousemove', onRulerDrag);
  window.addEventListener('mouseup', stopRulerDrag);
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Drag handlers for horizontal band (moves both rulers, preserving delta)
 */
function onBandDrag(event) {
  if (!draggingBand || !chartContainer.value || !xScale) return;

  const rect = chartContainer.value.getBoundingClientRect();
  const mouseX = event.clientX - rect.left - margin.left;
  const dxPixels = mouseX - bandDragStartMouseX;

  const domainSpan = chartMaxX - chartMinX || 1;
  const dxDomain = (dxPixels / chartWidth) * domainSpan;

  let newLeft = bandInitialLeft + dxDomain;
  let newRight = bandInitialRight + dxDomain;
  const widthDomain = bandInitialRight - bandInitialLeft;

  // Clamp so left >= min, right <= max, preserving width where possible
  if (newLeft < chartMinX) {
    const shift = chartMinX - newLeft;
    newLeft += shift;
    newRight += shift;
  }
  if (newRight > chartMaxX) {
    const shift = chartMaxX - newRight;
    newLeft += shift;
    newRight += shift;
  }

  // in extreme cases width > domain, clamp both
  if (widthDomain > domainSpan) {
    newLeft = chartMinX;
    newRight = chartMaxX;
  }

  internalLeftRulerX.value = newLeft;
  internalRightRulerX.value = newRight;

  emit('update:leftRulerX', newLeft);
  emit('update:rightRulerX', newRight);

  updateRulers();
}

function stopBandDrag() {
  if (!draggingBand) return;
  draggingBand = false;
  window.removeEventListener('mousemove', onBandDrag);
  window.removeEventListener('mouseup', stopBandDrag);
}

function startBandDrag(event) {
  if (!chartContainer.value || !xScale) return;
  if (internalLeftRulerX.value === null || internalRightRulerX.value === null) {
    return;
  }

  draggingBand = true;

  const rect = chartContainer.value.getBoundingClientRect();
  bandDragStartMouseX = event.clientX - rect.left - margin.left;
  bandInitialLeft = internalLeftRulerX.value;
  bandInitialRight = internalRightRulerX.value;

  window.addEventListener('mousemove', onBandDrag);
  window.addEventListener('mouseup', stopBandDrag);
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Click handler: snap marker to nearest original curve point if within a pixel threshold.
 */
function handleChartClick(event) {
  const seriesArr = getSeriesArray();
  if (!xScale || !yScale || seriesArr.length === 0 || !chartContainer.value)
    return;

  const rect = chartContainer.value.getBoundingClientRect();
  const mouseX = event.clientX - rect.left - margin.left;
  const mouseY = event.clientY - rect.top - margin.top;

  // only accept clicks inside the chart area
  if (mouseX < 0 || mouseX > chartWidth || mouseY < 0 || mouseY > chartHeight) {
    return;
  }

  const MAX_DISTANCE_PX = 10; // click radius threshold
  let nearestPoint = null;
  let nearestDist2 = Infinity;

  // search nearest raw data point across all series in screen coordinates
  seriesArr.forEach((series) => {
    if (!series.data || !series.data.length) return;

    series.data.forEach((d) => {
      const px = xScale(d.time);
      const py = yScale(d.flow);
      const dx = px - mouseX;
      const dy = py - mouseY;
      const dist2 = dx * dx + dy * dy;

      if (dist2 < nearestDist2) {
        nearestDist2 = dist2;
        nearestPoint = d;
      }
    });
  });

  if (!nearestPoint) return;

  const dist = Math.sqrt(nearestDist2);
  if (dist > MAX_DISTANCE_PX) {
    // click too far away from any point -> ignore
    return;
  }

  // snap marker to this point's x (time)
  emit('update:markerX', nearestPoint.time);
}

/**
 * Main chart drawing
 */
function drawChart() {
  if (!chartContainer.value) return;

  // Clear existing chart first so a stale chart from a previous series is
  // removed even when the new series has no data (e.g. no ROI drawn yet).
  d3.select(chartContainer.value).selectAll('svg').remove();

  const seriesArr = getSeriesArray();
  if (seriesArr.length === 0) return;

  // Preprocess series with zero crossings
  const processedSeries = seriesArr.map(series => ({
    ...series,
    processedData: withZeroCrossings(series.data),
  }));

  // All points for scaling
  const allPoints = seriesArr.flatMap(s => s.data);
  if (allPoints.length === 0) return;

  // Get container dimensions
  const containerRect = chartContainer.value.getBoundingClientRect();
  const width = containerRect.width - margin.left - margin.right;
  const height = containerRect.height - margin.top - margin.bottom;
  if (width <= 0 || height <= 0) return;

  chartWidth = width;
  chartHeight = height;

  // Root SVG
  const svgRoot = d3
    .select(chartContainer.value)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .attr('role', 'img')
    .attr('aria-label', props.title || 'Flow rate over time chart');

  const svg = svgRoot
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Scales across all series (X uses true min/max)
  const minTime = d3.min(allPoints, d => d.time) ?? 0;
  const maxTime = d3.max(allPoints, d => d.time) ?? 1;

  xScale = d3.scaleLinear().domain([minTime, maxTime]).range([0, width]);

  chartMinX = minTime;
  chartMaxX = maxTime;

  const minFlow = d3.min(allPoints, d => d.flow) ?? 0;
  const maxFlow = d3.max(allPoints, d => d.flow) ?? 0;

  // Asymmetric Y domain with padding
  let yMin = minFlow;
  let yMax = maxFlow;

  if (yMin === yMax) {
    // avoid zero span
    yMin -= 1;
    yMax += 1;
  }

  const span = yMax - yMin;
  const paddingFactor = 0.1; // 10% padding
  yMin -= span * paddingFactor;
  yMax += span * paddingFactor;

  yScale = d3.scaleLinear().domain([yMin, yMax]).range([height, 0]);

  // Grid
  if (props.showGrid) {
    // X grid
    svg
      .append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(8).tickSize(-height).tickFormat(''));

    // Y grid
    svg
      .append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).ticks(8).tickSize(-width).tickFormat(''));
  }

  // Zero baseline (only if 0 is within the visible domain)
  if (yMin < 0 && yMax > 0) {
    const zeroY = yScale(0);
    svg
      .append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', zeroY)
      .attr('y2', zeroY)
      .attr('stroke', 'var(--md-outline, #B0B0C0)')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .attr('opacity', 0.7);
  }

  const defs = svg.append('defs');

  // Series: gradients + areas + lines + points
  processedSeries.forEach((series, idx) => {
    const baseColor = series.color || props.color;
    const positiveGradientId = `flow-gradient-pos-${gradientSuffix}-${idx}`;
    const negativeGradientId = `flow-gradient-neg-${gradientSuffix}-${idx}`;

    // Gradients for this series
    const positiveGradient = defs
      .append('linearGradient')
      .attr('id', positiveGradientId)
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '0%')
      .attr('y2', '100%');

    positiveGradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', baseColor)
      .attr('stop-opacity', 0.6);

    positiveGradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', baseColor)
      .attr('stop-opacity', 0.12);

    const negativeGradient = defs
      .append('linearGradient')
      .attr('id', negativeGradientId)
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '0%')
      .attr('y2', '100%');

    negativeGradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', baseColor)
      .attr('stop-opacity', 0.1);

    negativeGradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', baseColor)
      .attr('stop-opacity', 0.35);

    // Group for this series
    const seriesGroup = svg.append('g').attr('class', `series series-${idx}`);

    const line = d3
      .line()
      .x(d => xScale(d.time))
      .y(d => yScale(d.flow))
      .curve(d3.curveMonotoneX);

    // For area baseline: if 0 is visible, use 0; else use the nearest domain edge
    let baselineValue;
    if (yMin < 0 && yMax > 0) {
      baselineValue = 0;
    }
    else if (yMax <= 0) {
      baselineValue = yMax; // all non-positive -> baseline at top
    }
    else {
      baselineValue = yMin; // all non-negative -> baseline at bottom
    }
    const baselineY = yScale(baselineValue);

    const areaPositive = d3
      .area()
      .x(d => xScale(d.time))
      .y0(baselineY)
      .y1(d => yScale(d.flow))
      .defined(d => d.flow >= 0)
      .curve(d3.curveMonotoneX);

    const areaNegative = d3
      .area()
      .x(d => xScale(d.time))
      .y0(baselineY)
      .y1(d => yScale(d.flow))
      .defined(d => d.flow <= 0)
      .curve(d3.curveMonotoneX);

    // Positive area
    seriesGroup
      .append('path')
      .datum(series.processedData)
      .attr('fill', `url(#${positiveGradientId})`)
      .attr('d', areaPositive)
      .attr('pointer-events', 'none');

    // Negative area
    seriesGroup
      .append('path')
      .datum(series.processedData)
      .attr('fill', `url(#${negativeGradientId})`)
      .attr('d', areaNegative)
      .attr('pointer-events', 'none');

    // Main line
    seriesGroup
      .append('path')
      .datum(series.processedData)
      .attr('fill', 'none')
      .attr('stroke', baseColor)
      .attr('stroke-width', 2.5)
      .attr('stroke-linecap', 'round')
      .attr('d', line)
      .attr('pointer-events', 'none');

    // Points (original data)
    if (props.showPoints && series.data && series.data.length) {
      seriesGroup
        .selectAll('circle')
        .data(series.data)
        .enter()
        .append('circle')
        .attr('cx', d => xScale(d.time))
        .attr('cy', d => yScale(d.flow))
        .attr('r', 3)
        .attr('fill', baseColor)
        .attr('stroke', '#FFFFFF')
        .attr('stroke-width', 0.2)
        .attr('pointer-events', 'none');
    }
  });

  // Draw marker + marker indicators + rulers once as part of full chart render
  drawMarkerLine(svg);
  drawMarkerIndicators(svg, seriesArr);
  drawRulers(svg, seriesArr);

  // Axes
  const xAxis = d3.axisBottom(xScale).ticks(8);
  const yAxis = d3.axisLeft(yScale).ticks(6);

  svg
    .append('g')
    .attr('transform', `translate(0,${height})`)
    .call(xAxis)
    .style('font-size', '11px');

  svg.append('g').call(yAxis).style('font-size', '11px');

  // Axis labels
  svg
    .append('text')
    .attr('x', width / 2)
    .attr('y', height + margin.bottom - 5)
    .attr('text-anchor', 'middle')
    .style('font-size', '12px')
    .style('font-weight', '500')
    .style('fill', 'var(--md-on-surface-variant, #5F5F6B)')
    .text(props.xLabel);

  svg
    .append('text')
    .attr('transform', 'rotate(-90)')
    .attr('x', -height / 2)
    .attr('y', -margin.left + 15)
    .attr('text-anchor', 'middle')
    .style('font-size', '12px')
    .style('font-weight', '500')
    .style('fill', 'var(--md-on-surface-variant, #5F5F6B)')
    .text(props.yLabel);

  // Invisible overlay – does NOT intercept events
  svg
    .append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'transparent')
    .attr('pointer-events', 'none');
}

onMounted(() => {
  // initial sync of internal rulers with props
  internalLeftRulerX.value = props.leftRulerX;
  internalRightRulerX.value = props.rightRulerX;

  nextTick(drawChart);

  if (chartContainer.value) {
    let resizeTimeout = null;
    resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        drawChart();
      }, 50);
    });
    resizeObserver.observe(chartContainer.value);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  // cleanup possible drag listeners
  stopRulerDrag();
  stopBandDrag();
});

// Data changes (deep if you mutate in-place)
watch(
  () => props.data,
  () => {
    nextTick(drawChart);
  },
  { deep: true },
);

// Style/behavior props (heavy redraw)
watch(
  () => [props.color, props.showPoints, props.showGrid],
  () => {
    nextTick(drawChart);
  },
);

// Marker updates: line + indicators
watch(
  () => props.markerX,
  () => {
    nextTick(updateMarkerLineAndIndicators);
  },
);

// Sync internal rulers from props when parent changes them
watch(
  () => props.leftRulerX,
  (val) => {
    internalLeftRulerX.value = val;
    nextTick(updateRulers);
  },
);

watch(
  () => props.rightRulerX,
  (val) => {
    internalRightRulerX.value = val;
    nextTick(updateRulers);
  },
);
</script>

<template>
  <div class="flow-diagram-container simple-panel">
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
    <div
      ref="chartContainer"
      class="chart-container"
      @click="handleChartClick"
    >
      <div
        v-if="rulerVolumeSummary.length"
        class="ruler-volume-summary"
      >
        <div
          v-for="entry in rulerVolumeSummary"
          :key="entry.id"
          class="ruler-volume-row"
        >
          <span
            class="ruler-volume-swatch"
            :style="{ backgroundColor: entry.color }"
          />
          <span class="ruler-volume-label">
            {{ entry.id }}
          </span>
          <span class="ruler-volume-values">
            in<span class="ruler-volume-num">{{ formatVolume(entry.inside) }}</span>ml
            out<span class="ruler-volume-num">{{ formatVolume(entry.outside) }}</span>ml
          </span>
        </div>
      </div>
    </div>

    <div class="brush-mode-controls">
      <button title="Take Screenshot" @click="takeScreenshot">
        📸
      </button>
    </div>

    <ScreenshotDialog
      :is-open="screenshotDialog"
      :image-data-url="screenshotDataUrl"
      :title="title"
      @update:dialog-open="screenshotDialog = $event"
      @save="handleScreenshotSave"
      @discard="handleScreenshotDiscard"
    />
  </div>
</template>

<style scoped>
.flow-diagram-container {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px 18px;
  border-radius: 16px;
  border: 1px solid var(--md-outline-variant, #d0cfe5);
  background-color: var(--md-surface-container-lowest, #fdfbff);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.diagram-title {
  margin: 0 0 10px 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--md-on-surface, #1c1b1f);
  padding-bottom: 8px;
  border-bottom: 1px solid var(--md-surface-container-highest, #e0e0f0);
}

.chart-container {
  flex: 1;
  min-height: 0;
  position: relative;
  width: 100%;
}

.ruler-volume-summary {
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 0;
  background: transparent;
  pointer-events: none;
  font-size: 10px;
  line-height: 1.2;
  color: var(--md-on-surface-variant, #5f5f6b);
}

.ruler-volume-row {
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
}

.ruler-volume-swatch {
  width: 6px;
  height: 6px;
  border-radius: 1px;
  flex-shrink: 0;
}

.ruler-volume-label {
  font-weight: 500;
  color: inherit;
}

.ruler-volume-values {
  color: inherit;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

.ruler-volume-num {
  display: inline-block;
  min-width: 5ch;
  text-align: right;
  white-space: pre;
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

/* Subtle grid styling */
:deep(.grid line) {
  stroke: var(--md-outline-variant, #d0cfe5);
  stroke-opacity: 0.7;
  stroke-dasharray: 2, 2;
}

:deep(.grid path) {
  stroke-width: 0;
}

/* Axis styling */
:deep(.domain) {
  stroke: var(--md-outline, #b0b0c0);
}

:deep(.tick line) {
  stroke: var(--md-outline-variant, #d0cfe5);
}

:deep(.tick text) {
  fill: var(--md-on-surface-variant, #5f5f6b);
}

.brush-mode-controls {
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  gap: 5px;
  align-items: center;
  z-index: 3;
}

.brush-mode-controls button {
  border-radius: 3px;
  color: white;
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  padding: 0;
}
</style>
